import React, { useContext, useEffect, useState } from 'react';
import {
  createSearchParams,
  NavLink,
  useLocation,
  useNavigate,
} from 'react-router-dom';
import { Section } from './navbar.interfaces';
import { useFirebaseSignOut } from '../../firebase/hooks';
import { pageIds } from 'utilities/constants';
import { useGetPage } from 'hooks/useGetPage';
import { useNavbarOptions } from 'hooks/useNavbarOptions';
import { AuthContext } from 'auth/context/AuthContext';
import Footer from 'components/footer/footerComponent';
import { useClearAllSavedDnaScannedDataForUserIdMutation } from 'graphql/generated/hasura';
import { ReactComponent as CloseIcon } from 'assets/icons/iconClose.svg';
import { ReactComponent as ChevronUpIcon } from 'assets/icons/chevron-up.svg';
import { ReactComponent as ChevronDownIcon } from 'assets/icons/chevron-down.svg';
import { ReactComponent as LogoutIcon } from 'assets/icons/logout.svg';

import {
  SUPER_ADMIN_APPOINTMENTS,
  SUPER_ADMIN_PATIENTS,
  SUPER_ADMIN_PROVIDERS,
  SUPER_ADMIN_TESTS,
} from '../../utilities/routes';

interface NavbarMobileProps {
  closeNavbarMobile: () => void;
}

const SuperAdminNavbarMobile: React.FC<NavbarMobileProps> = ({
  closeNavbarMobile,
}) => {
  const { data: locale, loading } = useGetPage({
    locale: 'en',
    pageId: pageIds.NAVBAR_COMPONENT,
  });
  const { loadingOptions, SuperAdminAccountOptions, SuperAdminAccountRoutes } =
    useNavbarOptions();

  const { user } = useContext(AuthContext);
  const location = useLocation();
  const navigate = useNavigate();
  const [selectedSection, setSelectedSection] = useState<Section>();
  const [isMyAccountOptionsOpen, setMyAccountOptionsOpen] =
    useState<boolean>(false);
  const signOutUser = useFirebaseSignOut();

  const [clearAllSavedDnaScannedDataForUserIdMutation] =
    useClearAllSavedDnaScannedDataForUserIdMutation({
      variables: {
        userId: user?.uuid || '',
      },
    });

  useEffect(() => {
    const route = location.pathname.split('/')[1];
    setSelectedSection((route as Section) || Section.Dashboard);
  }, [location]);

  const isSectionSelected = (section: Section): boolean =>
    selectedSection === section;

  const handleMyAccountOptions = async (optionSelected: string) => {
    switch (optionSelected) {
      case locale?.superAdminAccountOptions.logOut:
        await clearAllSavedDnaScannedDataForUserIdMutation().catch((e) =>
          console.log('Error on cleaning DNA saved data', e),
        );
        signOutUser();
        break;
      case locale?.superAdminAccountOptions?.changePassword:
        navigate({
          search: createSearchParams({
            'password-change': 'true',
          }).toString(),
        });
        closeNavbarMobile();
        break;

      default: {
        closeNavbarMobile();
      }
    }
  };

  if ((loading && !locale) || loadingOptions) return null;

  return (
    <div className="flex flex-col items-center p-0 bg-white">
      <div className="flex flex-row h-[100px] items-center justify-center p-2.5 gap-2.5">
        <div className="flex flex-col items-center justify-center p-px w-12 h-12">
          <CloseIcon
            className="w-12 h-12 stroke-med-gray"
            onClick={closeNavbarMobile}
          />
        </div>
      </div>

      <div className="flex flex-col w-[300px] items-center p-b[60px]">
        <hr className="flex flex-row w-full items-center h-px bg-black-blur" />

        <NavLink
          to={SUPER_ADMIN_APPOINTMENTS}
          className="flex flex-row w-full h-[50px] items-center p-0 gap-2.5"
          onClick={closeNavbarMobile}
        >
          <div
            className={`flex flex-row w-1.5 h-full items-start p-0 ${
              isSectionSelected(Section.AllAppointments) ? 'bg-clc-blue' : ''
            }`}
          />
          <div
            className={`flex items-center text-base font-bold ${
              isSectionSelected(Section.AllAppointments)
                ? 'text-clc-blue'
                : 'text-btn-black'
            }`}
          >
            {locale?.appointments}
          </div>
        </NavLink>

        <hr className="flex flex-row w-full items-center h-px bg-black-blur" />

        <NavLink
          to={SUPER_ADMIN_TESTS}
          className="flex flex-row w-full h-[50px] items-center p-0 gap-2.5"
          onClick={closeNavbarMobile}
        >
          <div
            className={`flex flex-row w-1.5 h-full items-start p-0 ${
              isSectionSelected(Section.TestKits) ? 'bg-clc-blue' : ''
            }`}
          />
          <div
            className={`flex items-center uppercase text-base font-bold ${
              isSectionSelected(Section.TestKits)
                ? 'text-clc-blue'
                : 'text-btn-black'
            }`}
          >
            {locale?.testKits}
          </div>
        </NavLink>

        <hr className="flex flex-row w-full items-center h-px bg-black-blur" />

        <NavLink
          to={SUPER_ADMIN_PATIENTS}
          className="flex flex-row w-full h-[50px] items-center p-0 gap-2.5"
          onClick={closeNavbarMobile}
        >
          <div
            className={`flex flex-row w-1.5 h-full items-start p-0 ${
              isSectionSelected(Section.Patients) ? 'bg-clc-blue' : ''
            }`}
          />
          <div
            className={`flex items-center uppercase text-base font-bold ${
              isSectionSelected(Section.Patients)
                ? 'text-clc-blue'
                : 'text-btn-black'
            }`}
          >
            {locale?.patients}
          </div>
        </NavLink>

        <hr className="flex flex-row w-full items-center h-px bg-black-blur" />

        <NavLink
          to={SUPER_ADMIN_PROVIDERS}
          className="flex flex-row w-full h-[50px] items-center p-0 gap-2.5"
          onClick={closeNavbarMobile}
        >
          <div
            className={`flex flex-row w-1.5 h-full items-start p-0 ${
              isSectionSelected(Section.Providers) ? 'bg-clc-blue' : ''
            }`}
          />
          <div
            className={`flex items-center uppercase text-base font-bold ${
              isSectionSelected(Section.Providers)
                ? 'text-clc-blue'
                : 'text-btn-black'
            }`}
          >
            {locale?.providers}
          </div>
        </NavLink>

        <hr className="flex flex-row w-full items-center h-px bg-black-blur" />

        <div
          className="flex flex-row w-full h-[50px] items-center p-0 gap-2.5"
          onClick={() => setMyAccountOptionsOpen(!isMyAccountOptionsOpen)}
        >
          <div
            className={`flex flex-row w-1.5 h-full items-start p-0 ${
              isSectionSelected(Section.MyAccount) ? 'bg-clc-blue' : ''
            }`}
          />
          <div
            className={`flex items-center uppercase text-base font-bold w-[250px] ${
              isSectionSelected(Section.MyAccount)
                ? 'text-clc-blue'
                : 'text-btn-black'
            }`}
          >
            {locale?.myAccount}
          </div>
          {isMyAccountOptionsOpen ? (
            <ChevronUpIcon className="w-[18px] h-2.5 fill-current" />
          ) : (
            <ChevronDownIcon className="w-[18px] h-2.5 fill-current" />
          )}
        </div>

        {isMyAccountOptionsOpen && (
          <div className="flex flex-col w-full items-start pb-2.5">
            {SuperAdminAccountOptions.map((option, index) => {
              if (option === locale?.superAdminAccountOptions?.logOut)
                return null;
              return SuperAdminAccountRoutes[option] ? (
                <NavLink
                  to={`${SuperAdminAccountRoutes[option]}`}
                  key={index}
                  className="flex flex-row h-10 text-base text-med-gray items-center pl-[37px]"
                  onClick={() => handleMyAccountOptions(`${option}`)}
                >
                  {option}
                </NavLink>
              ) : (
                <div
                  key={index}
                  onClick={() => handleMyAccountOptions(`${option}`)}
                  className="flex flex-row h-10 text-base text-med-gray items-center pl-[37px]"
                >
                  {option}
                </div>
              );
            })}
          </div>
        )}

        <hr className="flex flex-row w-full items-center h-px bg-black-blur" />

        <div
          className="flex flex-row w-full pl-4 h-[50px] text-btn-black text-base font-bold uppercase items-center p-0 gap-2.5"
          onClick={() =>
            handleMyAccountOptions(locale?.superAdminAccountOptions?.logOut)
          }
        >
          <LogoutIcon />
          {locale?.superAdminAccountOptions?.logOut}
        </div>

        <hr className="flex flex-row w-full items-center h-px bg-black-blur" />

        {/* {isAdminRole && (
          <div className="flex flex-row items-center mt-8 cursor-pointer">
            <AdminRoleToggle />
          </div>
        )} */}
      </div>
      <Footer type={'navbar'} closeNavbarMobile={closeNavbarMobile} />
    </div>
  );
};

export default SuperAdminNavbarMobile;
